import type { ReactElement } from 'react';
import { Tag } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { LinkCell, type OwnLink } from '../../../shared/ui/LinkCell.js';
import type { EntityKind } from '../../../shared/ui/EntityLink.js';
import { formatDate, formatDateTime } from '../../../shared/format/entity-detail.js';
import { formatPersonName } from '../../../shared/format/index.js';
import type { MeetingItem } from '../api/meetings.client.js';
import {
  meetingParticipantLabel,
  meetingParticipantTagStyle,
  STATUS_LABEL,
} from '../lib/meetingMeta.js';
import './meeting.css';

const DAY_MS = 86_400_000;

/** Сайдбар карточки встречи: сводка, связи и участники — как у остальных карточек. */
export function MeetingSidebar({ meeting }: { meeting: MeetingItem }): ReactElement {
  const { t } = useTranslation();
  const prefix = 'crm.features.features.meetings.pages.meetingcardpage.';
  const refs: { key: EntityKind; own: OwnLink | null }[] = [
    { key: 'deal', own: meeting.deal ? { id: meeting.deal.id, label: meeting.deal.title } : null },
    {
      key: 'contact',
      own: meeting.contact
        ? {
            id: meeting.contact.id,
            label: formatPersonName(meeting.contact.firstName, meeting.contact.lastName),
          }
        : null,
    },
    {
      key: 'company',
      own: meeting.company ? { id: meeting.company.id, label: meeting.company.name } : null,
    },
    { key: 'task', own: meeting.task ? { id: meeting.task.id, label: meeting.task.title } : null },
    {
      key: 'document',
      own: meeting.document ? { id: meeting.document.id, label: meeting.document.title } : null,
    },
    {
      key: 'project',
      own: meeting.project ? { id: meeting.project.id, label: meeting.project.name } : null,
    },
  ];
  const linked = refs.filter((ref) => ref.own !== null);
  // Начало: как срок в сводке задачи — значением идёт дата со временем, а подпись
  // под ней называет состояние. Красный — только у незакрытой прошедшей встречи
  // («просрочено»); закрытая или отменённая встреча просто прошла.
  const startsAtMs = Date.parse(meeting.startsAt);
  const isPast = startsAtMs < Date.now();
  const isOverdue = isPast && meeting.status === 'scheduled';
  const daysAgo = isPast ? Math.max(1, Math.floor((Date.now() - startsAtMs) / DAY_MS)) : 0;
  const startsHint = !isPast
    ? null
    : isOverdue
      ? daysAgo > 30
        ? t('crm.entity_detail.overdue_since', { date: formatDate(meeting.startsAt) })
        : t('crm.entity_detail.overdue_days', { count: daysAgo })
      : t(`${prefix}прошла_дней_назад`, { count: daysAgo });
  return (
    <>
      <section className="entity-panel">
        <h2 className="entity-section-title">{t('crm.entity_detail.summary')}</h2>
        <div className="meeting-detail-metrics">
          <EntityMetric
            testId="meeting-metric-starts"
            label={t(`${prefix}начало`)}
            value={formatDateTime(meeting.startsAt)}
            hint={startsHint}
            {...(isOverdue ? { hintStyle: { color: 'var(--ant-color-error)' } } : {})}
          />
          <EntityMetric
            testId="meeting-metric-ends"
            label={t(`${prefix}окончание`)}
            value={formatDateTime(meeting.endsAt)}
          />
          <EntityMetric
            testId="meeting-metric-status"
            label={t(`${prefix}статус`)}
            value={STATUS_LABEL[meeting.status]}
          />
          <EntityMetric
            testId="meeting-metric-timezone"
            label={t(`${prefix}часовой_пояс`)}
            value={meeting.timezone}
          />
        </div>
      </section>
      {linked.length > 0 ? (
        <section className="entity-panel">
          <h2 className="entity-section-title">{t('crm.entity_detail.links')}</h2>
          <EntityFieldList
            fields={linked.map((ref) => ({
              key: ref.key,
              label: t(`crm.shared.entity_type.${ref.key}`),
              value: <LinkCell kind={ref.key} own={ref.own} />,
            }))}
          />
        </section>
      ) : null}
      <section className="entity-panel">
        <h2 className="entity-section-title">
          {t(`${prefix}участники`)} ({meeting.participants.length})
        </h2>
        {meeting.participants.length === 0 ? (
          <span className="entity-meta">{t(`${prefix}участников_пока_нет`)}</span>
        ) : (
          <div className="meeting-detail-participants" data-testid="meeting-participants">
            {meeting.participants.map((participant) => (
              <Tag key={participant.id} style={meetingParticipantTagStyle(participant)}>
                {meetingParticipantLabel(participant)}
              </Tag>
            ))}
          </div>
        )}
      </section>
      <section className="entity-panel">
        <h2 className="entity-section-title">{t('crm.features.company_detail.details')}</h2>
        <EntityFieldList
          fields={[
            {
              key: 'location',
              label: t(`${prefix}место`),
              value: meeting.location ?? null,
            },
            {
              key: 'created',
              label: t('crm.entity_detail.created'),
              value: formatDateTime(meeting.createdAt),
            },
            {
              key: 'updated',
              label: t('crm.entity_detail.updated'),
              value: formatDateTime(meeting.updatedAt),
            },
          ]}
        />
      </section>
    </>
  );
}
